/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable, { Column, Row } from "components/DataTable";
import { BannerContext } from "components/SideBar";
import { useToaster } from "contexts/toasterContext";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import apiService from "services/apiService";

interface EmployeeOption {
  value: string;
  label: string;
}

interface SalaryHistoryRow extends Row {
  displayName: string;
  rev: number;
  id: number;
  employeeEmail: string;
  annualCtc: number;
  basicSalary: number;
  fbtAllowance: number;
  hra: number;
  variableAllowance: number;
  halfYearBonus: number | null;
  performanceBonus: number | null;
  gratuity: number;
  mediclaimSumAssured: number | null;
  providentFundEmployer: number;
  taxOnProfession: number;
  providentFundEmployee: number;
  tds: number;
  appraisalType: string | null;
  salaryAdvance: number | null;
  appraisalPercentage: number | null;
  modifiedDate: number;
  netTakeHome: number;
}

// const breadCrumbList = [
//   { name: "Salary", link: "salary-history" },
//   { name: "Salary History", link: "#" },
// ];

const SalaryHistory: React.FC = () => {
  const { addToast } = useToaster();

  // View mode controls whether we show the global list or a specific employee's history
  const [viewMode, setViewMode] = useState<"GLOBAL" | "SPECIFIC">("GLOBAL");

  // Dropdown States
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [employeePage, setEmployeePage] = useState(0);
  const [hasMoreEmployees, setHasMoreEmployees] = useState(true);

  // Table & Pagination States
  const [rows, setRows] = useState<SalaryHistoryRow[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [isServerPagination, setIsServerPagination] = useState(true);

  // --- Utility Functions ---
  const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toNullableString = (value: unknown): string | null => {
    if (value === null || value === undefined || value === "") return null;
    return String(value);
  };

  const formatTimestampToDDMMYYYY = (value: unknown): string => {
    const timestamp = Number(value);
    if (!Number.isFinite(timestamp) || timestamp <= 0) return "-";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB");
  };

  const toNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // --- UseEffects ---

  // 1. Debounce Dropdown Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(employeeSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [employeeSearch]);

  // 2. Fetch Dropdown Data (Triggered on mount and when search changes)
  useEffect(() => {
    fetchDropdownEmployees(0, debouncedSearch, false);
  }, [debouncedSearch]);

  // 3. Fetch Global Table Data (Triggered on page change if in GLOBAL mode)
  useEffect(() => {
    if (viewMode === "GLOBAL") {
      fetchGlobalSalaries(currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage, viewMode]);

  const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-110px)] min-h-[300px]"
    : "h-[calc(100vh-80px)] min-h-[300px]";
  // --- Fetch Logic ---

  // Manages ONLY the "Select Employee" Dropdown data
  const fetchDropdownEmployees = async (page: number, search: string, append = false) => {
    setIsLoadingEmployees(true);
    try {
      const response = await apiService.get(
        `/api/users?page=${page}&pageSize=15&search=${encodeURIComponent(search)}`,
      );
      const content = response.data.content ?? [];
      const options = content.map((employee: any) => ({
        value: employee.email,
        label: employee.displayName || employee.email,
      }));

      setEmployees((prev) => (append ? [...prev, ...options] : options));
      setEmployeePage(page);
      setHasMoreEmployees(!response.data.last);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Manages ONLY the Default Table Data (All users)
  const fetchGlobalSalaries = async (page: number, size: number) => {
    setIsLoadingHistory(true);
    setIsServerPagination(true);
    try {
      const response = await apiService.get(
        `/api/users?page=${page}&pageSize=${size}`
      );

      const content = response.data.content ?? [];
      const total = response.data.totalElements ?? content.length;

      const defaultSalaryData: SalaryHistoryRow[] = content.map((user: any, index: number) => {
        const item = user.salary || {}; // Safely handle users with no salary object
        return {
          displayName: String(user.displayName ?? user.email ?? ""),
          id: toNumber(item.id ?? Date.now() + index),
          rev: toNumber(item.rev),
          employeeEmail: String(user.email ?? ""),
          annualCtc: toNumber(item.annualCtc),
          basicSalary: toNumber(item.basicSalary),
          fbtAllowance: toNumber(item.fbtAllowance),
          hra: toNumber(item.hra),
          variableAllowance: toNumber(item.variableAllowance),
          halfYearBonus: toNullableNumber(item.halfYearBonus),
          performanceBonus: toNullableNumber(item.performanceBonus),
          gratuity: toNumber(item.gratuity),
          mediclaimSumAssured: toNullableNumber(item.mediclaimSumAssured),
          providentFundEmployer: toNumber(item.providentFundEmployer),
          taxOnProfession: toNumber(item.taxOnProfession),
          providentFundEmployee: toNumber(item.providentFundEmployee),
          tds: toNumber(item.tds),
          appraisalType: toNullableString(item.appraisalType),
          salaryAdvance: toNullableNumber(item.salaryAdvance),
          appraisalPercentage: toNullableNumber(item.appraisalPercentage),
          modifiedDate: toNumber(item.modifiedDate),
          netTakeHome: toNumber(item.netTakeHome),
        };
      });

      setRows(defaultSalaryData);
      setTotalItems(total);
    } catch (error) {
      console.error("Error fetching global salaries:", error);
      addToast("Failed to load salaries", "error");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Manages ONLY Specific User Data (When search is clicked)
  const handleSearch = () => {
    if (!selectedEmployee?.value) {
      addToast("Please select an employee", "error");
      return;
    }

    setIsLoadingHistory(true);
    setViewMode("SPECIFIC");
    apiService
      .get(`/api/salary/history/${encodeURIComponent(selectedEmployee.value)}`)
      .then((response) => {
        const responseData = response.data?.content ?? response.data;
        const dataArray = Array.isArray(responseData) ? responseData : [];

        const history: SalaryHistoryRow[] = dataArray.map(
          (item: Record<string, unknown>, index: number) => ({
            displayName: String(selectedEmployee.label || item.userName || item.employeeEmail || ""),
            id: toNumber(item.id ?? Date.now() + index),
            rev: toNumber(item.rev),
            employeeEmail: String(item.employeeEmail ?? ""),
            annualCtc: toNumber(item.annualCtc),
            basicSalary: toNumber(item.basicSalary),
            fbtAllowance: toNumber(item.fbtAllowance),
            hra: toNumber(item.hra),
            variableAllowance: toNumber(item.variableAllowance),
            halfYearBonus: toNullableNumber(item.halfYearBonus),
            performanceBonus: toNullableNumber(item.performanceBonus),
            gratuity: toNumber(item.gratuity),
            mediclaimSumAssured: toNullableNumber(item.mediclaimSumAssured),
            providentFundEmployer: toNumber(item.providentFundEmployer),
            taxOnProfession: toNumber(item.taxOnProfession),
            providentFundEmployee: toNumber(item.providentFundEmployee),
            tds: toNumber(item.tds),
            appraisalType: toNullableString(item.appraisalType),
            salaryAdvance: toNullableNumber(item.salaryAdvance),
            appraisalPercentage: toNullableNumber(item.appraisalPercentage),
            modifiedDate: toNumber(item.modifiedDate),
            netTakeHome: toNumber(item.netTakeHome),
          }),
        );

        setRows(history);
        setTotalItems(history.length);
        setCurrentPage(0);
        setIsServerPagination(false);
      })
      .catch((error) => {
        console.error("Error fetching salary history:", error);
        setRows([]);
        setTotalItems(0);
        addToast("Failed to load salary history", "error");
      })
      .finally(() => {
        setIsLoadingHistory(false);
      });
  };

  // --- Handlers ---

  const handleEmployeeChange = (option: EmployeeOption | null) => {
    setSelectedEmployee(option);

    if (!option) {
      setEmployeeSearch("");
      setViewMode("GLOBAL");
      setCurrentPage(0);
    }
  };

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage((prev) => (prev === newItemsPerPage ? prev : newItemsPerPage));
    setCurrentPage(0);
  }, []);

  const handleServerPageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage - 1);
  }, []);

  const columns: Column[] = useMemo(
    () => [
      { key: "displayName", label: "Employee Name", bold: true },
      { key: "rev", label: "Rev" },
      { key: "annualCtc", label: "Annual CTC", bold: true },
      { key: "basicSalary", label: "Basic Salary" },
      { key: "netTakeHome", label: "Net Take Home", bold: true },
      { key: "variableAllowance", label: "Variable Allowance" },
      { key: "gratuity", label: "Gratuity" },
      { key: "tds", label: "TDS" },
      {
        key: "modifiedDate",
        label: "Modified Date",
        bold: true,
        render: (value) => formatTimestampToDDMMYYYY(value),
      },
    ],
    [],
  );

  const isDarkTheme =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");

  const salaryHistorySelectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 9999,
    }),
    control: (base: Record<string, unknown>) => ({
      ...base,
      minHeight: 42,
      borderColor: isDarkTheme ? "#475569" : "#cbd5e1",
      backgroundColor: isDarkTheme ? "#334155" : "#ffffff",
      boxShadow: "none",
    }),
    valueContainer: (base: Record<string, unknown>) => ({
      ...base,
      paddingLeft: 12,
      paddingRight: 12,
    }),
    indicatorSeparator: (base: Record<string, unknown>) => ({
      ...base,
      display: "none",
    }),
    singleValue: (base: Record<string, unknown>) => ({
      ...base,
      color: isDarkTheme ? "#e2e8f0" : "#0f172a",
    }),
    input: (base: Record<string, unknown>) => ({
      ...base,
      color: isDarkTheme ? "#e2e8f0" : "#0f172a",
    }),
    placeholder: (base: Record<string, unknown>) => ({
      ...base,
      color: isDarkTheme ? "#e2e8f0" : "#64748b",
    }),
    menu: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 20,
      backgroundColor: isDarkTheme ? "#1e293b" : "#ffffff",
      color: isDarkTheme ? "#e2e8f0" : "#0f172a",
    }),
    menuList: (base: Record<string, unknown>) => ({
      ...base,
      backgroundColor: isDarkTheme ? "#1e293b" : "#ffffff",
      color: isDarkTheme ? "#e2e8f0" : "#0f172a",
    }),
    option: (
      base: Record<string, unknown>,
      state: { isFocused: boolean; isSelected: boolean },
    ) => ({
      ...base,
      backgroundColor:
        state.isFocused || state.isSelected
          ? isDarkTheme
            ? "#334155"
            : "#e2e8f0"
          : isDarkTheme
            ? "#1e293b"
            : "#ffffff",
      color: isDarkTheme ? "#e2e8f0" : "#0f172a",
    }),
    dropdownIndicator: (base: Record<string, unknown>) => ({
      ...base,
      color: isDarkTheme ? "#cbd5e1" : "#64748b",
    }),
    clearIndicator: (base: Record<string, unknown>) => ({
      ...base,
      color: isDarkTheme ? "#cbd5e1" : "#64748b",
    }),
  };

  const controls = (
    <>
      <div className="w-72">
        <Select<EmployeeOption, false>
          options={employees}
          value={selectedEmployee}
          onChange={handleEmployeeChange}
          placeholder="Select Employee"
          isDisabled={isLoadingHistory}
          isLoading={isLoadingEmployees}
          isClearable
          isSearchable
          classNamePrefix="salary-history-select"
          styles={salaryHistorySelectStyles}
          noOptionsMessage={() => "No employees found"}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
          onMenuScrollToBottom={() => {
            if (hasMoreEmployees && !isLoadingEmployees) {
              fetchDropdownEmployees(employeePage + 1, employeeSearch, true);
            }
          }}
          onInputChange={(value, meta) => {
            if (meta.action === "input-change") {
              setEmployeeSearch(value);
            }
          }}
          filterOption={null}
        />
      </div>

      <button
        type="button"
        onClick={handleSearch}
        // Disabled if loading, OR if no employee is selected in the dropdown
        disabled={isLoadingHistory || isLoadingEmployees || !selectedEmployee}
        // Removed the outline class and applied solid background/text colors directly
        className="bg-blue-600 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-all hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
      >
        {isLoadingHistory ? "Searching..." : "Search"}
      </button>
    </>
  );

  return (
    // FIX: Using flex-col and h-full on the outermost container, 
    // and flex-1 with min-h-0 strictly restricts the table inside, removing the outer scroll
    <div className="flex flex-col h-full text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      {/* <div className="shrink-0">
        <BreadCrumb BreadCrumbList={breadCrumbList} />
      </div> */}
      <div className="flex-1 min-h-0">
        <DataTable
          heightClass={dynamicHeightClass}
          // title="Salary History"
          data={rows}
          columns={columns}
          leftSideButtons={controls}
          loading={isLoadingHistory}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage + 1}
          totalItems={totalItems}
          serverSidePagination={isServerPagination}
          onPageChange={handleServerPageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          hideSearchBar
        />
      </div>
    </div>
  );
};

export default SalaryHistory;